import { useEffect, useState, useRef, useCallback } from "react";
import { doc, getDoc } from "firebase/firestore";
import styled from "styled-components";

import { ResultsBox } from "../components/theme/QuizBox";
import {
  GradientSpan,
  H1,
  P1,
  P2,
  P3,
} from "../components/theme/text/Headings";
import { Black01, gradientColors, White08 } from "../components/theme/colors";
import { StyledLink } from "./Home";
import { ProPicSm, SecPicSm } from "../components/theme/ProfilePics";

import { db } from "../services/firebase.config";
import { useAuth } from "../services/AuthContext";
import remoteQuizService from "../services/RemoteQuizService";

// OPTIMIZED: Import centralized cache system
import dataCache from "../services/DataCache";
import { useNotifications } from "../services/NotificationContext";
import mixpanelService from "../services/MixpanelService";
import postHogService from "../services/PostHogService";

import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const InfoBox = styled.div`
  border-radius: 10px;
  padding: 10px 60px;
  width: 100%;
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

export const FlexPic = styled.div`
  flex: 1;
`;

export const FlexText = styled.div`
  flex: 10;
  text-align: left;
`;

export const FlexArrow = styled.div`
  flex: 1;
`;

export const FlexRow = styled.div`
  display: flex;
  align-items: left; /* optional: vertical alignment */
  gap: 5px;
  padding: 20px 0 15px;
  border-bottom: 1px solid ${Black01};
  background-color: ${(props) => (props.$isUnread ? "#f8f9fa" : "transparent")};
  border-radius: ${(props) => (props.$isUnread ? "8px" : "0")};
  margin: ${(props) => (props.$isUnread ? "0 -10px" : "0")};
  padding: ${(props) => (props.$isUnread ? "20px 10px 15px" : "20px 0 15px")};
`;

const InboxContent = ({ refreshTrigger }) => {
  const { currentUserState } = useAuth();
  const { markAsRead, isUnread } = useNotifications();

  const [dataLoaded, setDataLoaded] = useState(false);
  const [sharedQuizzes, setSharedQuizzes] = useState([]);
  const [quizDetails, setQuizDetails] = useState([]);

  // OPTIMIZED: Use ref to prevent infinite loops
  const currentUserRef = useRef(currentUserState);
  currentUserRef.current = currentUserState;

  // Dynamic quiz config - reads from cloud with gradientColors fallback
  const [quizConfig, setQuizConfig] = useState({});

  // Build quiz config from cloud
  useEffect(() => {
    const buildQuizConfig = async () => {
      const config = {};

      // First, add fallback entries from gradientColors (for existing quizzes)
      for (const [quizType, colors] of Object.entries(gradientColors)) {
        config[quizType] = {
          url: `/${quizType}/results`,
          background: colors.background || "#667eea",
          transitionBackground: colors.transitionBackground || "#764ba2",
          title: quizType
            .split("-")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" "),
        };
      }

      // Then overlay remote configs (takes precedence)
      try {
        const remoteConfigs = await remoteQuizService.getAllQuizConfigs();
        if (remoteConfigs) {
          for (const [quizType, remoteConfig] of Object.entries(
            remoteConfigs
          )) {
            const colors =
              remoteConfig.gradientColors || gradientColors[quizType] || {};
            config[quizType] = {
              url: remoteConfig.resultsRoute || `/quiz/${quizType}/results`,
              background: colors.background || "#667eea",
              transitionBackground: colors.transitionBackground || "#764ba2",
              title:
                remoteConfig.displayInfo?.title ||
                remoteConfig.titleWord ||
                quizType,
            };
          }
        }
      } catch (error) {
        console.log("Error loading remote quiz config for inbox:", error);
      }

      setQuizConfig(config);
    };

    buildQuizConfig();
  }, []);

  // Handle clicking on a result - mark as read and track
  const handleResultClick = (resultId, quizData, otherUserData) => {
    markAsRead(resultId);

    // Track viewing vibe check from inbox
    const inboxViewProps = {
      quiz_type: quizData?.quizName || "unknown",
      quiz_name: quizConfig[quizData?.quizName]?.title || quizData?.quizName,
      viewer_id: currentUserState?.uid,
      other_user_id: otherUserData?.uid,
      other_user_name: otherUserData?.displayName,
      shared_quiz_id: resultId,
      source: "inbox",
      was_unread: isUnread(resultId),
      timestamp: new Date().toISOString(),
    };
    mixpanelService.track("Vibe Check Viewed", inboxViewProps);
    postHogService.track("Vibe Check Viewed", inboxViewProps);
  };

  // OPTIMIZED: Extract fetchProfileData as a reusable function for refreshing
  const fetchProfileData = useCallback(
    async (forceRefresh = false) => {
      if (!currentUserState) return;

      try {
        let profileData;

        if (forceRefresh) {
          // Force fresh data from Firebase
          const profileDocRef = doc(
            db,
            "public-profiles",
            currentUserState.uid
          );
          const profileDoc = await getDoc(profileDocRef);
          profileData = profileDoc.exists() ? profileDoc.data() : null;

          // Update cache with fresh data
          if (profileData) {
            dataCache.setUserProfile(currentUserState.uid, profileData);
          }
        } else {
          // OPTIMIZED: Check centralized cache first
          profileData = dataCache.getUserProfile(currentUserState.uid);

          if (!profileData) {
            const profileDocRef = doc(
              db,
              "public-profiles",
              currentUserState.uid
            );
            const profileDoc = await getDoc(profileDocRef);
            profileData = profileDoc.exists() ? profileDoc.data() : null;

            // OPTIMIZED: Cache in centralized system
            if (profileData) {
              dataCache.setUserProfile(currentUserState.uid, profileData);
            }
          }
        }

        if (profileData) {
          const sharedQuizIds = profileData.shared || [];

          setSharedQuizzes(sharedQuizIds);
          if (sharedQuizIds.length === 0) {
            setDataLoaded(true);
          }
        } else {
          setDataLoaded(true);
        }
      } catch (error) {
        setDataLoaded(true);
      }
    },
    [currentUserState]
  );

  // Initial load
  useEffect(() => {
    fetchProfileData();
  }, [fetchProfileData]);

  // Refresh when parent component triggers it (like tab switching)
  useEffect(() => {
    if (refreshTrigger && currentUserState) {
      console.log("🔄 Inbox refresh triggered by parent component");
      fetchProfileData(true); // Force refresh
    }
  }, [refreshTrigger, fetchProfileData, currentUserState]);

  // Real-time updates: refresh when app becomes visible/focused
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUserState) {
        // App became visible, refresh inbox data
        console.log("📱 App became visible, refreshing inbox...");
        fetchProfileData(true); // Force refresh
      }
    };

    const handleFocus = () => {
      if (currentUserState) {
        // Window gained focus, refresh inbox data
        console.log("🔍 Window focused, refreshing inbox...");
        fetchProfileData(true); // Force refresh
      }
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    // Cleanup
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [currentUserState, fetchProfileData]);

  useEffect(() => {
    const fetchSharedQuizzes = async () => {
      if (!sharedQuizzes.length) {
        setDataLoaded(true);
        return;
      }

      try {
        // OPTIMIZED: Process all quizzes in parallel with centralized caching
        const quizPromises = sharedQuizzes.map(async (quizId) => {
          // OPTIMIZED: Check centralized cache for shared quiz data
          let cachedQuizResult = dataCache.getSharedQuiz(quizId);

          if (cachedQuizResult) {
            return cachedQuizResult;
          }

          const quizDocRef = doc(db, "shared-quizzes", quizId);
          const quizDoc = await getDoc(quizDocRef);

          if (quizDoc.exists()) {
            const quizData = quizDoc.data();
            const otherUserUid =
              quizData.senderUid === currentUserRef.current.uid
                ? quizData.receiverUid
                : quizData.senderUid;

            // OPTIMIZED: Check centralized cache for other user's profile
            let otherUserData = dataCache.getUserProfile(otherUserUid);

            if (!otherUserData) {
              const otherUserProfileRef = doc(
                db,
                "public-profiles",
                otherUserUid
              );
              const otherUserProfileDoc = await getDoc(otherUserProfileRef);
              otherUserData = otherUserProfileDoc.exists()
                ? otherUserProfileDoc.data()
                : null;

              // OPTIMIZED: Cache other user's profile in centralized system
              if (otherUserData) {
                dataCache.setUserProfile(otherUserUid, otherUserData);
              }
            }

            if (otherUserData) {
              const result = {
                id: quizId,
                quizData,
                otherUserData,
              };

              // OPTIMIZED: Cache the complete quiz result in centralized system
              dataCache.setSharedQuiz(quizId, result);
              return result;
            }
          }
          return null;
        });

        // OPTIMIZED: Wait for all promises to resolve in parallel
        const quizResults = await Promise.all(quizPromises);
        const quizzes = quizResults.filter(Boolean);

        setQuizDetails(quizzes);
      } catch (error) {
      } finally {
        setDataLoaded(true);
      }
    };

    if (sharedQuizzes.length) {
      fetchSharedQuizzes();
    }
  }, [sharedQuizzes]);

  return (
    <>
      {quizDetails.length > 0
        ? quizDetails
            .slice()
            .reverse()
            .map((quizIdentifier, index) => {
              const quiz = quizIdentifier.quizData;
              const config = quiz ? quizConfig[quiz.quizName] || {} : {};

              return (
                <StyledLink
                  key={quizIdentifier.id}
                  to={`${config.url}?shared=${quizIdentifier.id}`}
                  onClick={() =>
                    handleResultClick(
                      quizIdentifier.id,
                      quizIdentifier.quizData,
                      quizIdentifier.otherUserData
                    )
                  }
                >
                  <FlexRow $isUnread={isUnread(quizIdentifier.id)}>
                    <FlexPic>
                      {quizIdentifier.otherUserData.photoURL ? (
                        <ProPicSm
                          src={quizIdentifier.otherUserData.photoURL}
                          $borderColor={config.background}
                        />
                      ) : (
                        <SecPicSm $borderColor={config.background}>
                          <H1>
                            {quizIdentifier.otherUserData.displayName
                              ? quizIdentifier.otherUserData.displayName[0]
                              : "?"}
                          </H1>
                        </SecPicSm>
                      )}
                    </FlexPic>
                    <ResultsBox key={quizIdentifier.id}>
                      <FlexText>
                        <P1
                          color={White08}
                          style={{
                            textDecoration: "none",
                          }}
                        >
                          <GradientSpan
                            $background={config.background}
                            $transitionBackground={config.transitionBackground}
                          >
                            {config.title || "A random"}
                          </GradientSpan>{" "}
                          quiz completed
                        </P1>
                        <P3 color={White08}>
                          From {quizIdentifier.otherUserData.displayName}
                        </P3>
                      </FlexText>
                    </ResultsBox>
                    <FlexArrow>
                      <ArrowForwardIosIcon
                        className="mr-15 mt-10"
                        style={{ color: `${White08}` }}
                      />
                    </FlexArrow>
                  </FlexRow>
                </StyledLink>
              );
            })
        : dataLoaded && (
            <InfoBox className="mt-150">
              <H1 color={White08}>No results found!</H1>
              <P2 color={White08} className="mt-15">
                Send your friends a link from the Quiz tab to see their
                responses here.
              </P2>
            </InfoBox>
          )}
    </>
  );
};

export default InboxContent;
